/**
 * Testimonial Block - JavaScript functionality
 * Handles carousel navigation with slide transitions, keyboard navigation,
 * touch/swipe support, and auto-advance functionality.
 */

const initTestimonial = () => {
	const testimonials = document.querySelectorAll('.block-testimonial--carousel');

	testimonials.forEach((testimonial) => {
		if (testimonial.dataset.initialized) return;
		initCarousel(testimonial);
		testimonial.dataset.initialized = 'true';
	});
};

/**
 * Initialize carousel functionality
 */
const initCarousel = (container) => {
	const section = container.querySelector('.block-testimonial') || container;
	const track = container.querySelector('.block-testimonial__track');
	if (!track) return;

	const slides = track.querySelectorAll('.block-testimonial__slide');
	// Get ALL dots and arrows (there may be multiple sets for silhouette layout)
	const dots = container.querySelectorAll('.block-testimonial__dot');
	const prevBtns = container.querySelectorAll('.block-testimonial__arrow--prev');
	const nextBtns = container.querySelectorAll('.block-testimonial__arrow--next');
	const autoAdvance = container.dataset.autoAdvance === 'true';
	const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	let currentIndex = 0;
	let autoplayInterval = null;
	let isPaused = false;
	const slideCount = slides.length;
	const autoplayDelay = 6000; // 6 seconds as per plan

	/**
	 * Go to a specific slide by translating the track
	 */
	const goToSlide = (index) => {
		// Wrap around
		if (index < 0) index = slideCount - 1;
		if (index >= slideCount) index = 0;

		// Slide the track
		track.style.transform = `translateX(-${index * 100}%)`;

		// Update ARIA attributes on slides
		slides.forEach((slide, i) => {
			const isActive = i === index;
			slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
		});

		// Update dots - use data-slide attribute to handle multiple dot sets
		dots.forEach((dot) => {
			const dotSlideIndex = parseInt(dot.dataset.slide, 10);
			const isActive = dotSlideIndex === index;
			dot.classList.toggle('block-testimonial__dot--active', isActive);
			dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
		});

		currentIndex = index;
	};

	/**
	 * Go to next slide
	 */
	const nextSlide = () => {
		goToSlide(currentIndex + 1);
	};

	/**
	 * Go to previous slide
	 */
	const prevSlide = () => {
		goToSlide(currentIndex - 1);
	};

	/**
	 * Start autoplay
	 */
	const startAutoplay = () => {
		if (!autoAdvance || prefersReducedMotion || isPaused) return;

		stopAutoplay();
		autoplayInterval = setInterval(nextSlide, autoplayDelay);
	};

	/**
	 * Stop autoplay
	 */
	const stopAutoplay = () => {
		if (autoplayInterval) {
			clearInterval(autoplayInterval);
			autoplayInterval = null;
		}
	};

	// Navigation button event listeners (handle multiple button sets)
	prevBtns.forEach((btn) => {
		btn.addEventListener('click', () => {
			prevSlide();
			stopAutoplay();
		});
	});

	nextBtns.forEach((btn) => {
		btn.addEventListener('click', () => {
			nextSlide();
			stopAutoplay();
		});
	});

	// Dot navigation
	dots.forEach((dot) => {
		dot.addEventListener('click', () => {
			const slideIndex = parseInt(dot.dataset.slide, 10);
			goToSlide(slideIndex);
			stopAutoplay();
		});
	});

	// Keyboard navigation
	container.addEventListener('keydown', (e) => {
		if (e.key === 'ArrowLeft') {
			e.preventDefault();
			prevSlide();
			stopAutoplay();
		} else if (e.key === 'ArrowRight') {
			e.preventDefault();
			nextSlide();
			stopAutoplay();
		}
	});

	// Pause on hover
	container.addEventListener('mouseenter', () => {
		isPaused = true;
		stopAutoplay();
	});

	container.addEventListener('mouseleave', () => {
		isPaused = false;
		startAutoplay();
	});

	// Pause on focus
	container.addEventListener('focusin', () => {
		isPaused = true;
		stopAutoplay();
	});

	container.addEventListener('focusout', (e) => {
		if (!container.contains(e.relatedTarget)) {
			isPaused = false;
			startAutoplay();
		}
	});

	// Touch/swipe support
	let touchStartX = 0;
	let touchEndX = 0;
	const swipeThreshold = 50;

	container.addEventListener('touchstart', (e) => {
		touchStartX = e.changedTouches[0].screenX;
	}, { passive: true });

	container.addEventListener('touchend', (e) => {
		touchEndX = e.changedTouches[0].screenX;
		const swipeDistance = touchEndX - touchStartX;

		if (Math.abs(swipeDistance) > swipeThreshold) {
			if (swipeDistance > 0) {
				prevSlide();
			} else {
				nextSlide();
			}
			stopAutoplay();
		}
	}, { passive: true });

	// IntersectionObserver for viewport-based autoplay
	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					startAutoplay();
				} else {
					stopAutoplay();
				}
			});
		},
		{ threshold: 0.5 }
	);

	observer.observe(container);

	// Start autoplay if enabled
	startAutoplay();
};

// DOM ready
document.addEventListener('DOMContentLoaded', initTestimonial);

// Gutenberg block preview support
if (window.acf) {
	window.acf.addAction('render_block_preview/type=testimonial', initTestimonial);
}

// Re-initialize on page visibility change (for autoplay resume)
document.addEventListener('visibilitychange', () => {
	if (document.visibilityState === 'visible') {
		// Reinitialize to restart autoplay where appropriate
		document.querySelectorAll('.block-testimonial--carousel[data-initialized="true"]').forEach((testimonial) => {
			testimonial.dataset.initialized = '';
		});
		initTestimonial();
	}
});
